import type { MeetPresence } from "../../hooks/api/meet";

/** Qué hacer cuando la psicóloga toca "Salir de la sesión" (política de tolerancia: ver docs de la videollamada propia, sección 5.2). */
export type ExitDecision =
  /** Salida directa */
  | { kind: "free" }
  /** El paciente nunca se conectó y todavía no pasó la tolerancia: se recuerda la política (y se ofrece reportar la ausencia) */
  | {
      kind: "wait-patient";
      minutesToTolerance: number;
      absenceReported: boolean;
    }
  /** El paciente estuvo conectado pero pasaron menos de los minutos mínimos de la sesión: confirmación breve */
  | { kind: "confirm-short"; minutesElapsed: number; minimumMin: number };

/**
 * La fricción es de la plataforma (psi-video solo informa los hechos). Sin datos de presencia (sin conexión o sin sala) no se
 * traba a nadie: salida directa. Nunca se bloquea del todo.
 */
export function decideExit(
  p: MeetPresence | null,
  absenceReported: boolean,
): ExitDecision {
  if (
    !p ||
    !p.roomExists ||
    p.minutesSinceStart === null ||
    p.minutesSinceStart < 0
  )
    return { kind: "free" };
  if (!p.patient.everJoined) {
    if (p.exit.toleranceOver) return { kind: "free" };
    return {
      kind: "wait-patient",
      minutesToTolerance: p.absence.minutesToTolerance,
      absenceReported,
    };
  }
  if (p.exit.belowMinimum)
    return {
      kind: "confirm-short",
      minutesElapsed: p.minutesSinceStart,
      minimumMin: p.rules.minSessionMin,
    };
  return { kind: "free" };
}

/** Borrador del registro de la sesión en el navegador (se pierde si no se guarda; nunca va a la telemetría). */
export const draftKey = (sessionId: string) => `meet:draft:${sessionId}`;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};
export const loadDraft = (
  sessionId: string,
  store: Store = localStorage,
): string | null => safe(() => store.getItem(draftKey(sessionId)), null);
export const saveDraft = (
  sessionId: string,
  text: string,
  store: Store = localStorage,
): void => {
  safe(
    () =>
      text
        ? store.setItem(draftKey(sessionId), text)
        : store.removeItem(draftKey(sessionId)),
    undefined,
  );
};
export const clearDraft = (
  sessionId: string,
  store: Store = localStorage,
): void => {
  safe(() => store.removeItem(draftKey(sessionId)), undefined);
};

export interface UrgencyInput {
  patientName: string;
  country?: string;
  timezone?: string;
  phone?: string | null;
  contacts: { name: string; phone: string; relationship: string }[];
  /** ISO de inicio de la sesión */
  sessionStart: string;
}

/** Texto del aviso de urgencia a CX con los datos del paciente. Solo arma el texto: no envía nada solo. */
export function buildUrgencyMessage(u: UrgencyInput): string {
  const lines = [
    "URGENCIA en sesión",
    `Paciente: ${u.patientName}`,
    u.country ? `País: ${u.country}` : null,
    u.timezone ? `Huso horario: ${u.timezone}` : null,
    u.phone ? `Teléfono del paciente: ${u.phone}` : null,
    `Sesión: ${u.sessionStart}`,
    u.contacts.length
      ? `Contactos de emergencia:\n${u.contacts.map((c) => `- ${c.name} (${c.relationship}): ${c.phone}`).join("\n")}`
      : "Sin contacto de emergencia cargado.",
  ];
  return lines.filter((l): l is string => !!l).join("\n");
}
